import type { ICellRendererParams } from '@ag-grid-community/core';
import { HIGHLIGHT_AMBER_BG, HIGHLIGHT_AMBER_COLOR } from '../../../styles';
import type { MaturityOrder } from '../deal-series/deal-series.types';

/** Column field names that can show the reprice-window price-change highlight. Use with refreshCells({ columns: [...], force: true }). */
export const PRICE_CHANGE_HIGHLIGHT_COLUMN_FIELDS: string[] = [
  'MATURITY_AMOUNT',
  'COUPON',
  'YIELD',
  'PRICE',
];

/** Inline style for the "text + padding" highlight box (works inside grid shadow DOM). */
const HIGHLIGHT_BOX_STYLE = `background-color:${HIGHLIGHT_AMBER_BG};color:${HIGHLIGHT_AMBER_COLOR.trim()};padding:0px 7px;border-radius:2px;display:inline-block;line-height:2;`;

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * Formatter for use with createPriceChangeCellRenderer: returns '' for pinned rows (e.g. totals),
 * otherwise formats the cell value. Use so column defs stay DRY.
 */
export function pinnedAwareFormatter(
  format: (value: unknown) => string,
): (params: ICellRendererParams<MaturityOrder>) => string {
  return (params) => (params.node?.isRowPinned() ? '' : format(params.value));
}

/**
 * Returns an AG Grid cell renderer that shows the formatted value and, when in reprice window
 * and the row's change flag is true, wraps it in a span with padding/background so only the
 * text (with padding) is highlighted, not the whole cell.
 */
export function createPriceChangeCellRenderer(
  getIsInRepriceWindow: () => boolean,
  flagField: keyof MaturityOrder,
  formatValue: (params: ICellRendererParams<MaturityOrder>) => string,
): (params: ICellRendererParams<MaturityOrder>) => string {
  return (params: ICellRendererParams<MaturityOrder>): string => {
    const formatted = formatValue(params);
    if (params.node?.isRowPinned?.()) return formatted;
    if (!getIsInRepriceWindow()) return formatted;
    const flagValue = params.data?.[flagField] as boolean | undefined;
    if (!flagValue) return formatted;
    return `<span style="${HIGHLIGHT_BOX_STYLE}">${escapeHtml(formatted)}</span>`;
  };
}

/**
 * Refreshes only the columns that can show the reprice-window highlight, so cell renderers run again.
 * Call when isInRepriceWindow (or equivalent) changes.
 */
export function refreshPriceChangeColumns(api: { refreshCells: (params?: { columns?: string[]; force?: boolean }) => void }): void {
  api.refreshCells({ columns: PRICE_CHANGE_HIGHLIGHT_COLUMN_FIELDS, force: true });
}
